// UltraFlex Admin - Email Template Management
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Mail, Plus, Edit3, Trash2, Eye, Save, X, Copy,
  Search, ChevronDown, Send, Code, FileText, Check,
  AlertTriangle, Zap, UserPlus, CreditCard, KeyRound
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  onboarding: { label: 'Onboarding', color: 'emerald', icon: UserPlus },
  transactional: { label: 'Transactional', color: 'blue', icon: Zap },
  marketing: { label: 'Marketing', color: 'violet', icon: Send },
  general: { label: 'General', color: 'gray', icon: Mail },
};

const AdminEmailTemplates = () => {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showTestSend, setShowTestSend] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState(null);
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchTemplates(); }, [catFilter]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params = catFilter ? `?category=${catFilter}` : '';
      const res = await fetch(`${API_URL}/api/email-templates${params}`, { headers });
      if (res.ok) setTemplates(await res.json());
    } catch {} finally { setLoading(false); }
  };

  const saveTemplate = async (data) => {
    const isUpdate = !!data.id;
    const url = isUpdate ? `${API_URL}/api/email-templates/${data.id}` : `${API_URL}/api/email-templates`;
    const method = isUpdate ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers, body: JSON.stringify(data) });
    if (res.ok) { fetchTemplates(); setEditing(null); setShowCreate(false); }
    else { const err = await res.json(); alert(err.detail || 'Save failed'); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    await fetch(`${API_URL}/api/email-templates/${id}`, { method: 'DELETE', headers });
    fetchTemplates();
  };

  const previewTemplate = async (slug) => {
    const res = await fetch(`${API_URL}/api/email-templates/preview/${slug}`, { method: 'POST', headers, body: JSON.stringify({}) });
    if (res.ok) { const d = await res.json(); setPreviewHtml(d.html); setPreviewing(slug); }
  };

  const sendTestEmail = async (slug) => {
    if (!testEmail) return;
    setTestResult(null);
    const res = await fetch(`${API_URL}/api/email-templates/send-test/${slug}`, {
      method: 'POST', headers, body: JSON.stringify({ to_email: testEmail, variables: {} })
    });
    setTestResult(await res.json());
  };

  const filtered = templates.filter(t =>
    (!search || t.name.toLowerCase().includes(search.toLowerCase()) || t.slug.includes(search.toLowerCase()))
  );

  const categories = ['', 'onboarding', 'transactional', 'marketing', 'general'];

  return (
    <div className="p-4 lg:p-8" data-testid="email-templates-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-uf-text" data-testid="email-templates-title">Email Templates</h1>
          <p className="text-uf-text-muted text-sm mt-1">Manage and customise all outgoing email templates</p>
        </div>
        <button onClick={() => { setShowCreate(true); setEditing({ slug: '', name: '', subject: '', html_body: '', description: '', wildcards: [], category: 'general', is_active: true }); }}
          className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm" data-testid="create-template-btn">
          <Plus className="w-4 h-4" />New Template
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-uf-text-muted" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates..."
            className="w-full pl-10 pr-3 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text text-sm" data-testid="template-search" />
        </div>
        <div className="flex gap-1 bg-uf-surface rounded-lg p-1">
          {categories.map(c => (
            <button key={c || 'all'} onClick={() => setCatFilter(c)} data-testid={`cat-filter-${c || 'all'}`}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${catFilter === c ? 'bg-uf-red text-white' : 'text-uf-text-muted hover:text-uf-text'}`}>
              {c ? CATEGORY_CONFIG[c]?.label || c : 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Template Cards */}
      {loading ? (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map(t => {
            const cat = CATEGORY_CONFIG[t.category] || CATEGORY_CONFIG.general;
            const CatIcon = cat.icon;
            return (
              <div key={t.id} className="bg-uf-surface rounded-xl border border-uf-border p-5 hover:border-uf-red/30 transition-colors" data-testid={`template-card-${t.slug}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg bg-${cat.color}-500/10 flex items-center justify-center`}>
                      <CatIcon className={`w-5 h-5 text-${cat.color}-400`} />
                    </div>
                    <div>
                      <h3 className="text-uf-text font-semibold text-sm">{t.name}</h3>
                      <p className="text-uf-text-muted text-xs font-mono">{t.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium bg-${cat.color}-500/10 text-${cat.color}-400`}>{cat.label}</span>
                    {!t.is_active && <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-red-500/10 text-red-400">Inactive</span>}
                  </div>
                </div>

                <p className="text-uf-text-muted text-xs mb-3">{t.description || 'No description'}</p>

                <div className="mb-3">
                  <p className="text-uf-text-muted text-[10px] uppercase tracking-wider mb-1">Subject</p>
                  <p className="text-uf-text text-xs bg-uf-surface-hi rounded px-2 py-1 font-mono">{t.subject}</p>
                </div>

                <div className="mb-4">
                  <p className="text-uf-text-muted text-[10px] uppercase tracking-wider mb-1">Wildcards</p>
                  <div className="flex flex-wrap gap-1">
                    {(t.wildcards || []).map(w => (
                      <span key={w} className="px-1.5 py-0.5 bg-uf-surface-hi rounded text-[10px] text-uf-text font-mono">{`{{${w}}}`}</span>
                    ))}
                    {(!t.wildcards || !t.wildcards.length) && <span className="text-uf-text-muted text-xs">None</span>}
                  </div>
                </div>

                <div className="flex gap-2 border-t border-uf-border pt-3">
                  <button onClick={() => previewTemplate(t.slug)} className="flex items-center gap-1 px-3 py-1.5 bg-uf-surface-hi rounded text-xs text-uf-text hover:text-uf-red transition-colors" data-testid={`preview-${t.slug}`}>
                    <Eye className="w-3 h-3" />Preview
                  </button>
                  <button onClick={() => setEditing(t)} className="flex items-center gap-1 px-3 py-1.5 bg-uf-surface-hi rounded text-xs text-uf-text hover:text-uf-red transition-colors" data-testid={`edit-${t.slug}`}>
                    <Edit3 className="w-3 h-3" />Edit
                  </button>
                  <button onClick={() => { setShowTestSend(t.slug); setTestEmail(''); setTestResult(null); }} className="flex items-center gap-1 px-3 py-1.5 bg-uf-surface-hi rounded text-xs text-uf-text hover:text-blue-400 transition-colors" data-testid={`test-send-${t.slug}`}>
                    <Send className="w-3 h-3" />Test Send
                  </button>
                  <button onClick={() => deleteTemplate(t.id)} className="flex items-center gap-1 px-3 py-1.5 bg-uf-surface-hi rounded text-xs text-uf-text hover:text-red-400 transition-colors ml-auto" data-testid={`delete-${t.slug}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
          {!filtered.length && (
            <div className="col-span-2 bg-uf-surface rounded-xl border border-uf-border p-12 text-center">
              <Mail className="w-12 h-12 mx-auto mb-4 text-uf-text-muted opacity-50" />
              <p className="text-uf-text-muted">No templates found</p>
            </div>
          )}
        </div>
      )}

      {/* Edit / Create Modal */}
      {editing && <TemplateEditor template={editing} onSave={saveTemplate} onClose={() => { setEditing(null); setShowCreate(false); }} isNew={showCreate} />}

      {/* Preview Modal */}
      {previewing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPreviewing(null)}>
          <div className="bg-uf-surface rounded-xl border border-uf-border w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-uf-border">
              <h3 className="text-uf-text font-semibold">Template Preview: {previewing}</h3>
              <button onClick={() => setPreviewing(null)} className="text-uf-text-muted hover:text-uf-text"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh] bg-white rounded-b-xl">
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </div>
        </div>
      )}

      {/* Test Send Modal */}
      {showTestSend && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowTestSend(null)}>
          <div className="bg-uf-surface rounded-xl border border-uf-border p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-uf-text font-semibold mb-4">Send Test Email</h3>
            <p className="text-uf-text-muted text-xs mb-3">Template: <span className="font-mono text-uf-text">{showTestSend}</span></p>
            <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="Recipient email address"
              className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-white text-sm mb-3 placeholder:text-zinc-500" data-testid="test-send-email-input" />
            <p className="text-uf-text-muted text-[10px] mb-3">Wildcards will be replaced with placeholder values [wildcard_name]</p>
            {testResult && (
              <div className={`p-3 rounded-lg mb-3 text-xs ${testResult.status === 'sent' ? 'bg-emerald-500/10 text-emerald-400' : testResult.status === 'skipped' ? 'bg-amber-500/10 text-amber-400' : 'bg-red-500/10 text-red-400'}`}>
                <p className="font-medium">{testResult.status === 'sent' ? 'Email sent successfully!' : testResult.status === 'skipped' ? 'Email skipped — Resend API key is not configured. Add RESEND_API_KEY to the environment to enable email sending.' : 'Error: ' + testResult.error}</p>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowTestSend(null)} className="px-4 py-2 text-uf-text-muted text-sm">Cancel</button>
              <button onClick={() => sendTestEmail(showTestSend)} className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm" data-testid="send-test-btn">
                <Send className="w-3 h-3" />Send Test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const TemplateEditor = ({ template, onSave, onClose, isNew }) => {
  const [form, setForm] = useState({
    id: template.id || undefined,
    slug: template.slug || '',
    name: template.name || '',
    subject: template.subject || '',
    html_body: template.html_body || '',
    description: template.description || '',
    wildcards: template.wildcards || [],
    category: template.category || 'general',
    is_active: template.is_active !== false,
  });
  const [wildcardInput, setWildcardInput] = useState('');
  const [activeEditorTab, setActiveEditorTab] = useState('visual');

  const addWildcard = () => {
    const w = wildcardInput.trim().replace(/[^a-z0-9_]/gi, '_').toLowerCase();
    if (w && !form.wildcards.includes(w)) {
      setForm({ ...form, wildcards: [...form.wildcards, w] });
      setWildcardInput('');
    }
  };

  const removeWildcard = (w) => {
    setForm({ ...form, wildcards: form.wildcards.filter(x => x !== w) });
  };

  const insertWildcard = (w) => {
    setForm({ ...form, html_body: form.html_body + `{{${w}}}` });
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-uf-surface rounded-xl border border-uf-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-uf-border">
          <h3 className="text-uf-text font-semibold">{isNew ? 'Create Template' : `Edit: ${form.name}`}</h3>
          <button onClick={onClose} className="text-uf-text-muted hover:text-uf-text"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-uf-text-muted text-xs block mb-1">Template Name *</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="editor-name" />
            </div>
            <div>
              <label className="text-uf-text-muted text-xs block mb-1">Slug (unique key) *</label>
              <input value={form.slug} onChange={e => setForm({...form, slug: e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase()})}
                disabled={!isNew} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm font-mono disabled:opacity-60" data-testid="editor-slug" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-uf-text-muted text-xs block mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="editor-category">
                <option value="onboarding">Onboarding</option>
                <option value="transactional">Transactional</option>
                <option value="marketing">Marketing</option>
                <option value="general">General</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})}
                  className="rounded border-uf-border" />
                <span className="text-uf-text text-sm">Active</span>
              </label>
            </div>
          </div>

          <div>
            <label className="text-uf-text-muted text-xs block mb-1">Description</label>
            <input value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="editor-description" />
          </div>

          <div>
            <label className="text-uf-text-muted text-xs block mb-1">Email Subject (supports wildcards)</label>
            <input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}
              className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm font-mono" data-testid="editor-subject" />
          </div>

          {/* Wildcards */}
          <div>
            <label className="text-uf-text-muted text-xs block mb-1">Wildcards</label>
            <div className="flex flex-wrap gap-1 mb-2">
              {form.wildcards.map(w => (
                <span key={w} className="flex items-center gap-1 px-2 py-1 bg-uf-surface-hi rounded text-xs text-uf-text font-mono group">
                  {`{{${w}}}`}
                  <button onClick={() => insertWildcard(w)} className="text-blue-400 hover:text-blue-300 opacity-0 group-hover:opacity-100" title="Insert into body"><Copy className="w-3 h-3" /></button>
                  <button onClick={() => removeWildcard(w)} className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={wildcardInput} onChange={e => setWildcardInput(e.target.value)} placeholder="new_wildcard_name"
                onKeyDown={e => e.key === 'Enter' && addWildcard()}
                className="flex-1 bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-1.5 text-uf-text text-xs font-mono" data-testid="wildcard-input" />
              <button onClick={addWildcard} className="px-3 py-1.5 bg-uf-surface-hi border border-uf-border rounded-lg text-xs text-uf-text hover:text-uf-red" data-testid="add-wildcard-btn">
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* HTML Body Editor */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-uf-text-muted text-xs">Email Body (HTML)</label>
              <div className="flex gap-1">
                <button onClick={() => setActiveEditorTab('visual')} className={`px-2 py-1 rounded text-[10px] ${activeEditorTab === 'visual' ? 'bg-uf-red text-white' : 'text-uf-text-muted'}`}>
                  <Eye className="w-3 h-3 inline mr-1" />Preview
                </button>
                <button onClick={() => setActiveEditorTab('code')} className={`px-2 py-1 rounded text-[10px] ${activeEditorTab === 'code' ? 'bg-uf-red text-white' : 'text-uf-text-muted'}`}>
                  <Code className="w-3 h-3 inline mr-1" />Code
                </button>
              </div>
            </div>
            {activeEditorTab === 'code' ? (
              <textarea value={form.html_body} onChange={e => setForm({...form, html_body: e.target.value})} rows={16}
                className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-xs font-mono resize-y" data-testid="editor-html-body" />
            ) : (
              <div className="bg-white rounded-lg p-4 border border-uf-border min-h-[200px] max-h-[400px] overflow-y-auto">
                <div dangerouslySetInnerHTML={{ __html: form.html_body }} />
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 justify-end p-4 border-t border-uf-border">
          <button onClick={onClose} className="px-4 py-2 text-uf-text-muted text-sm">Cancel</button>
          <button onClick={() => onSave(form)} className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm" data-testid="save-template-btn">
            <Save className="w-4 h-4" />{isNew ? 'Create Template' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminEmailTemplates;
